import { AddSensorInput } from '@repo/backend-types';
import { ImporterFeedback } from '@repo/frontend-types';

import { upsertSensors } from '@repo/front-end-services';
import Config from '../../common/Config';
interface SyncSensorsParams {
  accessToken: string;
  orgBID: string;
  sensorInputs: AddSensorInput[] | null;
  addImporterFeedback: (feedback: ImporterFeedback) => void;
}

/**
 * Syncs sensors to Dgraph after entities have been created.
 * This ensures the entity references can be properly established via @hasInverse.
 *
 * Should be called AFTER upsertTwinWithMetadataPreservation completes successfully.
 */
export async function syncSensorsWithFeedback({
  accessToken,
  orgBID,
  sensorInputs,
  addImporterFeedback,
}: SyncSensorsParams): Promise<void> {
  if (sensorInputs && sensorInputs.length > 0) {
    addImporterFeedback({
      intent: 'success',
      message: `Syncing sensors...`,
    });

    try {
      const sensorSyncReport = await upsertSensors(Config.middlewareApi, accessToken, {
        orgBid: orgBID,
        sensors: sensorInputs,
      });

      if (sensorSyncReport.success) {
        addImporterFeedback({
          intent: 'success',
          message: `Sensors synced (${sensorSyncReport.insertedCount} inserted, ${sensorSyncReport.updatedCount} updated)`,
        });

        // Display warnings from FML import validation
        // Note: Backend already includes ZERO_SENSORS_CREATED and SENSORS_NOT_LINKED_TO_ENTITY
        // warnings in the warnings array, so we only need to iterate through warnings once
        if (sensorSyncReport.warnings && sensorSyncReport.warnings.length > 0) {
          for (const warning of sensorSyncReport.warnings) {
            const affectedCount = warning.affectedBids?.length || 0;
            const affectedList =
              affectedCount > 3
                ? `${warning.affectedBids?.slice(0, 3).join(', ')}... and ${affectedCount - 3} more`
                : warning.affectedBids?.join(', ');

            addImporterFeedback({
              intent: 'warning',
              message: `⚠️ ${warning.message}${affectedList ? ` (${affectedList})` : ''}`,
            });
          }
        }
      } else {
        addImporterFeedback({
          intent: 'error',
          message: `Sensors sync error: ${sensorSyncReport.error}`,
        });
      }
    } catch (error) {
      addImporterFeedback({
        intent: 'error',
        message: `Error syncing sensors: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  } else {
    addImporterFeedback({
      intent: 'info',
      message: 'No sensors to sync',
    });
  }
}
